<script lang="ts">
	import {
		PROMO_COLLAPSE_MS,
		prefersReducedMotion,
		promoCollapse,
		promoFadeCollapse,
	} from 'src/helpers/ui_transitions';

	/**
	 * Collapses vertical space when content leaves so siblings below ease up
	 * into the gap (lists, widget stacks, etc.).
	 *
	 * Usage:
	 * ```svelte
	 * <CollapseGap when={visible} hold={hasChildExit} marginBottom="var(--spacing-m)">
	 *   …content…
	 * </CollapseGap>
	 * ```
	 *
	 * Call `lockHeight()` before clearing inner content on the last item so the
	 * outro still has a non-zero height to animate (bind:this on this component).
	 */
	/** Logical visibility — when false (and not held), runs height collapse outro. */
	export let when = false;
	/**
	 * Keep the host mounted while true even if `when` is false
	 * (e.g. in-flight per-item dismiss animations).
	 */
	export let hold = false;
	/** Collapse duration in ms (0 when prefers-reduced-motion). */
	export let duration = PROMO_COLLAPSE_MS;
	/** Fade opacity while collapsing. */
	export let fade = true;
	/**
	 * Bottom margin on the collapsing host so spacing closes with the height.
	 * Pass `null` / empty string to skip.
	 */
	export let marginBottom: string | null = 'var(--spacing-m, 16px)';
	export let className = '';

	let hostEl: HTMLDivElement | null = null;
	let mounted = false;
	let heightLockPx: number | null = null;

	$: collapseMs = prefersReducedMotion() ? 0 : duration;
	$: shouldShow = when || hold;

	$: if (shouldShow) {
		mounted = true;
		heightLockPx = null;
	} else if (mounted) {
		// Previous frame's DOM is still painted — lock before unmount.
		lockHeight();
		mounted = false;
	}

	$: hostStyle = [
		marginBottom ? `margin-bottom: ${marginBottom}` : '',
		heightLockPx != null ? `height: ${heightLockPx}px` : '',
	]
		.filter(Boolean)
		.join('; ');

	/** Snapshot current host height before inner content is cleared. */
	export function lockHeight() {
		if (heightLockPx != null) return;
		const h = hostEl?.offsetHeight ?? 0;
		if (h > 0) heightLockPx = h;
	}

	const runIn = (node: Element, params: { duration: number }) =>
		fade
			? promoFadeCollapse(node, params)
			: promoCollapse(node, { ...params, opacity: false });

	const runOut = (node: Element, params: { duration: number }) =>
		fade
			? promoFadeCollapse(node, params)
			: promoCollapse(node, { ...params, opacity: false });
</script>

{#if mounted}
	<div
		bind:this={hostEl}
		class="collapse-gap {className}"
		style={hostStyle}
		in:runIn={{ duration: collapseMs }}
		out:runOut={{ duration: collapseMs }}
	>
		<slot />
	</div>
{/if}

<style>
	.collapse-gap {
		width: 100%;
		overflow: hidden;
	}
</style>
